import { useState, useMemo, useCallback, useEffect } from 'react';
import { playBell } from '@/lib/bellSound';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { fetchCorrelationForReport } from '@/lib/correlationReport';
import { ArrowLeft, Upload, Search, TrendingUp, TrendingDown, AlertTriangle, RefreshCw, Mail, Filter, Download, Cloud, Activity, Clock, MailCheck, MailX, FileSpreadsheet } from 'lucide-react';
import { AutoSaveIndicator } from '@/components/AutoSaveIndicator';
import { SavedDataMenu } from '@/components/SavedDataMenu';
import { ComposedChart, Line, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, Brush } from 'recharts';
import { IchimokuSurveillanceChart, IchimokuResult } from '@/components/IchimokuSurveillanceChart';
import * as XLSX from 'xlsx';
import { useImportPersistence, formatImportTimestamp } from '@/hooks/use-import-persistence';
import { LastImportBadge } from '@/components/LastImportBadge';
import { LoadSavedDataDialog } from '@/components/LoadSavedDataDialog';

import { LoadSavedDataButton } from '@/components/LoadSavedDataButton';

interface SMAResult {
  ticker: string;
  currentPrice: number;
  sma2: number;
  sma12: number;
  sma50: number;
  sma200: number;
  signal2_12: 'BUY' | 'SELL' | 'NEUTRAL';
  signal50_200: 'BUY' | 'SELL' | 'NEUTRAL';
  crossover2_12: boolean;
  crossover50_200: boolean;
  bollingerUpper: number;
  bollingerMiddle: number;
  bollingerLower: number;
  bollingerSignal: 'BUY' | 'SELL' | 'NEUTRAL';
  priceHistory: { date: string; close: number }[];
  error?: string;
}

interface ScanSummary {
  total: number;
  buySignals: number;
  sellSignals: number;
  crossovers: number;
  errors: number;
}

interface IchimokuSummary {
  total: number;
  kumoBreakDown: number;
  withinKumo: number;
  kumoExitDown: number;
  tenkanTouch: number;
  kijunTouch: number;
  totalWithAlerts: number;
  errors: number;
}

const alertTypeLabels: Record<string, { label: string; color: string }> = {
  'KUMO_BREAK_DOWN': { label: 'Kumo Break ↓', color: 'bg-red-500' },
  'WITHIN_KUMO': { label: 'In Kumo', color: 'bg-yellow-500' },
  'KUMO_EXIT_DOWN': { label: 'Kumo Exit ↓', color: 'bg-red-600' },
  'TENKAN_TOUCH_UP': { label: 'Tenkan ↑', color: 'bg-green-500' },
  'TENKAN_TOUCH_DOWN': { label: 'Tenkan ↓', color: 'bg-orange-500' },
  'KIJUN_TOUCH_UP': { label: 'Kijun ↑', color: 'bg-blue-500' },
  'KIJUN_TOUCH_DOWN': { label: 'Kijun ↓', color: 'bg-purple-500' },
};

interface SMAImportData {
  id: string;
  user_id: string;
  total_stocks: number;
  stocks_with_signals: number;
  buy_signals: number;
  sell_signals: number;
  analysis_results: {
    smaResults: SMAResult[];
    dailyIchimokuResults: IchimokuResult[];
    weeklyIchimokuResults: IchimokuResult[];
    smaSummary: ScanSummary | null;
    dailyIchimokuSummary: IchimokuSummary | null;
    weeklyIchimokuSummary: IchimokuSummary | null;
  };
  imported_at: string;
  updated_at: string;
}

const SMASurveillance = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [tickers, setTickers] = useState<string[]>([]);
  
  // SMA state
  const [smaResults, setSmaResults] = useState<SMAResult[]>([]);
  const [smaSummary, setSmaSummary] = useState<ScanSummary | null>(null);
  const [selectedSmaStock, setSelectedSmaStock] = useState<SMAResult | null>(null);
  const [showCrossoversOnly, setShowCrossoversOnly] = useState(false);
  
  // Ichimoku state
  const [dailyIchimokuResults, setDailyIchimokuResults] = useState<IchimokuResult[]>([]);
  const [weeklyIchimokuResults, setWeeklyIchimokuResults] = useState<IchimokuResult[]>([]);
  const [dailyIchimokuSummary, setDailyIchimokuSummary] = useState<IchimokuSummary | null>(null);
  const [weeklyIchimokuSummary, setWeeklyIchimokuSummary] = useState<IchimokuSummary | null>(null);
  const [selectedIchimokuStock, setSelectedIchimokuStock] = useState<IchimokuResult | null>(null);
  const [ichimokuTimeframe, setIchimokuTimeframe] = useState<'daily' | 'weekly'>('daily');
  const [showAlertsOnly, setShowAlertsOnly] = useState(false);
  
  // Loading state
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeTab, setActiveTab] = useState<'sma' | 'ichimoku'>('sma');
  
  // On-demand price history loading
  const [loadingPriceHistory, setLoadingPriceHistory] = useState(false);
  const [loadingIchimokuHistory, setLoadingIchimokuHistory] = useState(false);

  // Import persistence hook
  const { lastImport, savedData, loading: loadingSaved, saveImport } = useImportPersistence<SMAImportData>('sma_surveillance_imports');
  
  // State for saved data dialog
  const [showSavedDataDialog, setShowSavedDataDialog] = useState(false);
  const [hasCheckedSavedData, setHasCheckedSavedData] = useState(false);
  
  // Track number of pre-loaded charts after restoration
  const [preloadedChartsCount, setPreloadedChartsCount] = useState<{ sma: number; ichimoku: number } | null>(null);
  
  // Email notification toggle - persisted in localStorage
  const [checklistEmailEnabled, setChecklistEmailEnabled] = useState<boolean>(() => {
    const stored = localStorage.getItem('sma-checklist-email-enabled');
    return stored === null ? true : stored === 'true';
  });
  const [reportRecipientEmail, setReportRecipientEmail] = useState(() => localStorage.getItem('sma_report_recipient') || '');
  
  const [correlationInReportEnabled, setCorrelationInReportEnabled] = useState<boolean>(() => {
    const stored = localStorage.getItem('sma-correlation-report-enabled');
    return stored === null ? true : stored === 'true';
  });

  const toggleCorrelationInReport = () => {
    const newValue = !correlationInReportEnabled;
    setCorrelationInReportEnabled(newValue);
    localStorage.setItem('sma-correlation-report-enabled', String(newValue));
    toast({
      title: newValue ? "Correlation in Reports Enabled" : "Correlation in Reports Disabled",
      description: newValue
        ? "Correlation matrix will be included in scan report emails"
        : "Correlation analysis skipped — faster report generation",
    });
  };
  
  const toggleChecklistEmail = () => {
    const newValue = !checklistEmailEnabled;
    setChecklistEmailEnabled(newValue);
    localStorage.setItem('sma-checklist-email-enabled', String(newValue));
    toast({
      title: newValue ? "Checklist Emails Enabled" : "Checklist Emails Disabled",
      description: newValue 
        ? "You will receive checklist emails after scans and restores" 
        : "Checklist emails are now disabled",
    });
  };

  // Check for autoLoad from navigation state (from Collected Data page)
  useEffect(() => {
    const state = location.state as { autoLoad?: boolean } | null;
    if (state?.autoLoad && savedData?.analysis_results && !loadingSaved) {
      handleLoadSavedData();
      // Clear the state to prevent re-loading on subsequent renders
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, savedData, loadingSaved]);

  // Check for saved data on mount and show dialog
  useEffect(() => {
    const state = location.state as { autoLoad?: boolean } | null;
    if (!loadingSaved && !hasCheckedSavedData && !state?.autoLoad) {
      if (savedData?.analysis_results && smaResults.length === 0) {
        setShowSavedDataDialog(true);
      }
      setHasCheckedSavedData(true);
    }
  }, [loadingSaved, savedData, hasCheckedSavedData, smaResults.length, location.state]);

  const handleLoadSavedData = async () => {
    if (savedData?.analysis_results) {
      const results = savedData.analysis_results;
      const runId = savedData.id;
      
      // Load SMA price histories from separate table
      let smaPriceHistoryMap: Record<string, { date: string; close: number }[]> = {};
      // Load Ichimoku price histories from separate table
      let ichimokuPriceHistoryMap: Record<string, IchimokuResult['priceHistory']> = {};
      
      if (runId) {
        // Fetch SMA price histories
        const { data: smaHistories } = await supabase
          .from('surveillance_price_history')
          .select('ticker, price_history')
          .eq('run_id', runId);
        
        if (smaHistories) {
          for (const ph of smaHistories) {
            smaPriceHistoryMap[ph.ticker] = (ph.price_history as { date: string; close: number }[]) || [];
          }
        }
        
        // Fetch Ichimoku price histories
        const { data: ichimokuHistories } = await supabase
          .from('ichimoku_price_history')
          .select('ticker, price_history')
          .eq('run_id', runId);
        
        if (ichimokuHistories) {
          for (const ph of ichimokuHistories) {
            ichimokuPriceHistoryMap[ph.ticker] = (ph.price_history as unknown as IchimokuResult['priceHistory']) || [];
          }
        }
      }
      
      // Merge price histories back into SMA results
      const smaResultsWithHistory = (results.smaResults || []).map(r => ({
        ...r,
        priceHistory: smaPriceHistoryMap[r.ticker] || r.priceHistory || []
      }));
      
      // Merge Ichimoku price histories back into results
      const dailyIchimokuWithHistory = (results.dailyIchimokuResults || []).map(r => ({
        ...r,
        priceHistory: ichimokuPriceHistoryMap[`daily_${r.ticker}`] || r.priceHistory || []
      }));
      
      const weeklyIchimokuWithHistory = (results.weeklyIchimokuResults || []).map(r => ({
        ...r,
        priceHistory: ichimokuPriceHistoryMap[`weekly_${r.ticker}`] || r.priceHistory || []
      }));
      
      setSmaResults(smaResultsWithHistory);
      setDailyIchimokuResults(dailyIchimokuWithHistory);
      setWeeklyIchimokuResults(weeklyIchimokuWithHistory);
      setSmaSummary(results.smaSummary || null);
      setDailyIchimokuSummary(results.dailyIchimokuSummary || null);
      setWeeklyIchimokuSummary(results.weeklyIchimokuSummary || null);
      
      const smaCount = Object.keys(smaPriceHistoryMap).length;
      const ichimokuCount = Object.keys(ichimokuPriceHistoryMap).length;
      
      // Set preloaded charts count for badge display
      setPreloadedChartsCount({ sma: smaCount, ichimoku: ichimokuCount });
      
      toast({
        title: "Data Loaded",
        description: `Restored: ${smaCount} SMA + ${ichimokuCount} Ichimoku chart histories ready.`,
      });
      
      // Send checklist email for restored data (if enabled)
      if (checklistEmailEnabled) {
        const { data: { user } } = await supabase.auth.getUser();
        let restoreCorrelationData = null;
        if (correlationInReportEnabled) {
          const restoreTickers = (smaResultsWithHistory as any[]).map((r) => String(r?.ticker || '')).filter((t) => t.length > 0);
          const uniqueRestoreTickers = restoreTickers.filter((t, i) => restoreTickers.indexOf(t) === i);
          restoreCorrelationData = await fetchCorrelationForReport(uniqueRestoreTickers);
        }
        supabase.functions.invoke('send-checklist-report', {
          body: {
            smaResults: smaResultsWithHistory,
            dailyIchimokuResults: dailyIchimokuWithHistory,
            weeklyIchimokuResults: weeklyIchimokuWithHistory,
            source: 'restore',
            userEmail: user?.email,
            recipientEmail: reportRecipientEmail.trim() || undefined,
            correlationData: restoreCorrelationData || undefined,
          }
        }).then(response => {
          if (response.error) {
            console.error('Failed to send checklist email:', response.error);
          } else {
            console.log('Checklist email sent for restored data');
            toast({
              title: "Checklist Email Sent",
              description: "Check List SMA 2/12 & Ichimoku emailed to you",
            });
          }
        });
      }
    }
    setShowSavedDataDialog(false);
  };

  const handleDeclineSavedData = () => {
    setShowSavedDataDialog(false);
  };

  // Fetch price history on-demand for a single stock
  const fetchPriceHistoryOnDemand = useCallback(async (ticker: string): Promise<{ date: string; close: number }[]> => {
    try {
      // Use the sma-surveillance edge function with a single ticker to get price history
      const { data, error } = await supabase.functions.invoke('sma-surveillance', {
        body: { tickers: [ticker] }
      });

      if (error) throw error;
      
      const result = data?.results?.[0];
      if (result?.priceHistory && result.priceHistory.length > 0) {
        return result.priceHistory;
      }
      return [];
    } catch (error) {
      console.error(`Failed to fetch price history for ${ticker}:`, error);
      return [];
    }
  }, []);

  // Handle clicking on an SMA stock row
  const handleSelectSmaStock = useCallback(async (result: SMAResult) => {
    // If priceHistory is empty (restored from DB), fetch it on-demand
    if (!result.priceHistory || result.priceHistory.length === 0) {
      setLoadingPriceHistory(true);
      toast({
        title: "Loading Chart Data",
        description: `Fetching price history for ${result.ticker}...`,
      });

      const priceHistory = await fetchPriceHistoryOnDemand(result.ticker);
      
      if (priceHistory.length > 0) {
        // Update the result with fetched price history
        const updatedResult = { ...result, priceHistory };
        
        // Also update it in the smaResults array so it's cached
        setSmaResults(prev => prev.map(r => 
          r.ticker === result.ticker ? updatedResult : r
        ));
        
        setSelectedSmaStock(updatedResult);
      } else {
        toast({
          title: "Chart Unavailable",
          description: `Could not fetch price history for ${result.ticker}`,
          variant: "destructive",
        });
        // Still show the modal with available data (no chart)
        setSelectedSmaStock(result);
      }
      setLoadingPriceHistory(false);
    } else {
      setSelectedSmaStock(result);
    }
  }, [fetchPriceHistoryOnDemand, toast]);

  // Fetch Ichimoku price history on-demand for a single stock/timeframe
  const fetchIchimokuHistoryOnDemand = useCallback(
    async (ticker: string, timeframe: 'daily' | 'weekly'): Promise<IchimokuResult['priceHistory']> => {
      try {
        const { data, error } = await supabase.functions.invoke('ichimoku-analysis', {
          body: { tickers: [ticker], timeframe },
        });
        if (error) throw error;

        const result = data?.results?.[0];
        if (result?.priceHistory && Array.isArray(result.priceHistory) && result.priceHistory.length > 0) {
          return result.priceHistory;
        }
        return [];
      } catch (error) {
        console.error(`Failed to fetch Ichimoku history for ${ticker} (${timeframe}):`, error);
        return [];
      }
    },
    [],
  );

  const handleSelectIchimokuStock = useCallback(
    async (result: IchimokuResult) => {
      if (!result.priceHistory || result.priceHistory.length === 0) {
        setLoadingIchimokuHistory(true);
        toast({
          title: 'Loading Chart Data',
          description: `Fetching Ichimoku history for ${result.ticker} (${result.timeframe})...`,
        });

        const priceHistory = await fetchIchimokuHistoryOnDemand(result.ticker, result.timeframe);

        if (priceHistory.length > 0) {
          const updated = { ...result, priceHistory };
          if (result.timeframe === 'daily') {
            setDailyIchimokuResults((prev) => prev.map((r) => (r.ticker === result.ticker ? updated : r)));
          } else {
            setWeeklyIchimokuResults((prev) => prev.map((r) => (r.ticker === result.ticker ? updated : r)));
          }
          setSelectedIchimokuStock(updated);
        } else {
          toast({
            title: 'Chart Unavailable',
            description: `Could not fetch Ichimoku history for ${result.ticker} (${result.timeframe})`,
            variant: 'destructive',
          });
          setSelectedIchimokuStock(result);
        }

        setLoadingIchimokuHistory(false);
      } else {
        setSelectedIchimokuStock(result);
      }
    },
    [fetchIchimokuHistoryOnDemand, toast],
  );

  const filteredSmaResults = useMemo(() => {
    if (!showCrossoversOnly) return smaResults;
    return smaResults.filter(r => r.crossover2_12 || r.crossover50_200);
  }, [smaResults, showCrossoversOnly]);

  const ichimokuResults = ichimokuTimeframe === 'daily' ? dailyIchimokuResults : weeklyIchimokuResults;
  const ichimokuSummary = ichimokuTimeframe === 'daily' ? dailyIchimokuSummary : weeklyIchimokuSummary;

  const filteredIchimokuResults = useMemo(() => {
    if (!showAlertsOnly) return ichimokuResults;
    return ichimokuResults.filter(r => r.alerts.length > 0);
  }, [ichimokuResults, showAlertsOnly]);

  // Export to CSV
  const exportToCSV = () => {
    if (filteredSmaResults.length === 0) return;
    
    const headers = [
      'Ticker', 'Price', 'SMA 2', 'SMA 12', 'Signal 2/12', 
      'SMA 50', 'SMA 200', 'Signal 50/200', 'BB Upper', 'BB Middle', 
      'BB Lower', 'Bollinger Signal', 'Crossover 2/12', 'Crossover 50/200'
    ];
    
    const rows = filteredSmaResults.map(r => [
      r.ticker,
      r.currentPrice,
      r.sma2,
      r.sma12,
      r.signal2_12,
      r.sma50,
      r.sma200,
      r.bollingerUpper,
      r.bollingerMiddle,
      r.bollingerLower,
      r.bollingerSignal,
      r.crossover2_12 ? 'Yes' : 'No',
      r.crossover50_200 ? 'Yes' : 'No'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `sma_ichimoku_scan_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast({
      title: "Export Complete",
      description: "CSV file downloaded successfully.",
    });
  };

  // Export to Excel
  const exportToExcel = () => {
    if (filteredSmaResults.length === 0) return;
    
    const smaData = filteredSmaResults.map(r => ({
      'Ticker': r.ticker,
      'Price': r.currentPrice,
      'SMA 2': r.sma2,
      'SMA 12': r.sma12,
      'Signal 2/12': r.signal2_12,
      'SMA 50': r.sma50,
      'SMA 200': r.sma200,
      'Signal 50/200': r.signal50_200,
      'BB Upper': r.bollingerUpper,
      'BB Middle': r.bollingerMiddle,
      'BB Lower': r.bollingerLower,
      'Bollinger Signal': r.bollingerSignal,
      'Crossover 2/12': r.crossover2_12 ? 'Yes' : 'No',
      'Crossover 50/200': r.crossover50_200 ? 'Yes' : 'No'
    }));
    
    const workbook = XLSX.utils.book_new();
    const smaSheet = XLSX.utils.json_to_sheet(smaData);
    XLSX.utils.book_append_sheet(workbook, smaSheet, 'SMA Results');
    
    // Add Ichimoku results
    if (dailyIchimokuResults.length > 0) {
      const ichimokuData = dailyIchimokuResults.map(r => ({
        'Ticker': r.ticker,
        'Price': r.currentPrice,
        'Tenkan-Sen': r.tenkanSen,
        'Kijun-Sen': r.kijunSen,
        'Senkou Span A': r.senkouSpanA,
        'Senkou Span B': r.senkouSpanB,
        'Alerts': r.alerts.map(a => a.type).join(', ') || 'None'
      }));
      const ichimokuSheet = XLSX.utils.json_to_sheet(ichimokuData);
      XLSX.utils.book_append_sheet(workbook, ichimokuSheet, 'Ichimoku Daily');
    }
    
    if (weeklyIchimokuResults.length > 0) {
      const weeklyData = weeklyIchimokuResults.map(r => ({
        'Ticker': r.ticker,
        'Price': r.currentPrice,
        'Tenkan-Sen': r.tenkanSen,
        'Kijun-Sen': r.kijunSen,
        'Senkou Span A': r.senkouSpanA,
        'Senkou Span B': r.senkouSpanB,
        'Alerts': r.alerts.map(a => a.type).join(', ') || 'None'
      }));
      const weeklySheet = XLSX.utils.json_to_sheet(weeklyData);
      XLSX.utils.book_append_sheet(workbook, weeklySheet, 'Ichimoku Weekly');
    }
    
    XLSX.writeFile(workbook, `sma_ichimoku_scan_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    toast({
      title: "Export Complete",
      description: "Excel file downloaded successfully.",
    });
  };

  // Parse CSV line handling quoted values
  const parseCsvLine = (line: string): string[] => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (const char of line) {
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    return values;
  };

  // CSV transform auto - automatically detects and extracts unique stock codes
  const handleCsvTransformAuto = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length === 0) {
          toast({
            title: "Empty File",
            description: "The CSV file appears to be empty",
            variant: "destructive",
          });
          return;
        }

        // Parse header
        const header = parseCsvLine(lines[0]).map(h => h.replace(/"/g, '').trim());
        
        // Parse ALL data rows
        const allData: string[][] = [];
        for (let i = 1; i < lines.length; i++) {
          const values = parseCsvLine(lines[i]).map(v => v.replace(/"/g, '').trim());
          while (values.length < header.length) values.push('');
          allData.push(values.slice(0, header.length));
        }
        
        // Auto-detect stock column
        const stockColumnNames = ['symbol', 'ticker', 'code', 'stock', 'symbole', 'action', 'isin', 'stock symbol', 'financial instrument'];
        let detectedColumnIndex = -1;
        
        const headerLower = header.map(h => h.toLowerCase());
        for (const name of stockColumnNames) {
          const idx = headerLower.findIndex(h => h.includes(name));
          if (idx !== -1) {
            detectedColumnIndex = idx;
            break;
          }
        }

        // Fall back to first column if no stock column detected
        if (detectedColumnIndex === -1) {
          detectedColumnIndex = 0;
        }

        // Extract unique tickers from detected column
        const seenTickers = new Set<string>();
        const uniqueTickers: string[] = [];
        let duplicatesRemoved = 0;
        
        for (const row of allData) {
          if (row[detectedColumnIndex]) {
            let ticker = row[detectedColumnIndex].trim().toUpperCase();
            
            // Skip empty values
            if (!ticker) continue;
            
            // Clean up common suffixes (e.g., ".US", ".L", etc.)
            ticker = ticker.replace(/\.(US|L|PA|DE|AS|MI|MC|TO|V|AX|HK|T|SS|SZ|NS|BO)$/i, '');
            
            // Remove any remaining whitespace
            ticker = ticker.trim();
            
            // Validate ticker format (1-10 alphanumeric chars, may include dots for class shares)
            if (ticker && /^[A-Z0-9.]{1,10}$/.test(ticker)) {
              // Only add if not already seen (ensures unique entries)
              if (!seenTickers.has(ticker)) {
                seenTickers.add(ticker);
                uniqueTickers.push(ticker);
              } else {
                duplicatesRemoved++;
              }
            }
          }
        }

        if (uniqueTickers.length === 0) {
          toast({
            title: "No Tickers Found",
            description: "Could not find valid stock codes in the file",
            variant: "destructive",
          });
          return;
        }

        // Set tickers and reset results
        setTickers(uniqueTickers);
        setSmaResults([]);
        setSmaSummary(null);
        setSelectedSmaStock(null);
        setDailyIchimokuResults([]);
        setWeeklyIchimokuResults([]);
        setDailyIchimokuSummary(null);
        setWeeklyIchimokuSummary(null);
        setSelectedIchimokuStock(null);
        setPreloadedChartsCount(null);
        
        const columnName = header[detectedColumnIndex] || `Column ${detectedColumnIndex + 1}`;
        toast({
          title: "CSV Transformed",
          description: `Extracted ${uniqueTickers.length} unique stock codes from "${columnName}"${duplicatesRemoved > 0 ? ` (${duplicatesRemoved} duplicates removed)` : ''}`,
        });
        
      } catch (error) {
        console.error('CSV parse error:', error);
        toast({
          title: "Error Processing CSV",
          description: "Could not parse the CSV file. Please check the format.",
          variant: "destructive",
        });
      }
    };
    reader.readAsText(file);
    
    event.target.value = '';
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Get column A values
        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
        const extractedTickers: string[] = [];
        
        for (let row = range.s.r; row <= range.e.r; row++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 }); // Column A
          const cell = worksheet[cellAddress];
          if (cell && cell.v && typeof cell.v === 'string') {
            const ticker = cell.v.trim().toUpperCase();
            if (ticker && ticker.length > 0 && ticker.length <= 10) {
              extractedTickers.push(ticker);
            }
          }
        }

        setTickers(extractedTickers);
        setSmaResults([]);
        setSmaSummary(null);
        setSelectedSmaStock(null);
        setDailyIchimokuResults([]);
        setWeeklyIchimokuResults([]);
        setDailyIchimokuSummary(null);
        setWeeklyIchimokuSummary(null);
        setSelectedIchimokuStock(null);
        
        toast({
          title: "File Loaded",
          description: `Found ${extractedTickers.length} tickers in column A`,
        });
      } catch (error) {
        toast({
          title: "Error Reading File",
          description: "Could not parse the Excel file",
          variant: "destructive",
        });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleScan = async () => {
    if (tickers.length === 0) {
      toast({
        title: "No Tickers",
        description: "Please upload an Excel file with tickers first",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setProgress(5);
    setSmaResults([]);
    setSmaSummary(null);
    setSelectedSmaStock(null);
    setDailyIchimokuResults([]);
    setWeeklyIchimokuResults([]);
    setDailyIchimokuSummary(null);
    setWeeklyIchimokuSummary(null);
    setSelectedIchimokuStock(null);

    try {
      // Step 1: SMA Analysis
      setProgress(15);
      const { data: smaData, error: smaError } = await supabase.functions.invoke('sma-surveillance', {
        body: { tickers }
      });

      if (smaError) throw smaError;
      setSmaResults(smaData.results || []);
      setSmaSummary(smaData.summary || null);
      
      setProgress(40);

      // Step 2: Daily Ichimoku Analysis
      const { data: dailyData, error: dailyError } = await supabase.functions.invoke('ichimoku-analysis', {
        body: { tickers, timeframe: 'daily' }
      });

      if (dailyError) throw dailyError;
      setDailyIchimokuResults(dailyData.results || []);
      setDailyIchimokuSummary(dailyData.summary || null);

      setProgress(70);

      // Step 3: Weekly Ichimoku Analysis
      const { data: weeklyData, error: weeklyError } = await supabase.functions.invoke('ichimoku-analysis', {
        body: { tickers, timeframe: 'weekly' }
      });

      if (weeklyError) throw weeklyError;
      setWeeklyIchimokuResults(weeklyData.results || []);
      setWeeklyIchimokuSummary(weeklyData.summary || null);

      setProgress(100);

      // Save slim results (no priceHistory) to main table, price histories go to separate table
      const persistedSmaResults = (smaData.results || []).map((r: SMAResult) => ({
        ...r,
        priceHistory: [],
      }));

      const persistedDailyIchimokuResults = (dailyData.results || []).map((r: IchimokuResult) => ({
        ...r,
        priceHistory: [],
      }));

      const persistedWeeklyIchimokuResults = (weeklyData.results || []).map((r: IchimokuResult) => ({
        ...r,
        priceHistory: [],
      }));
      
      // Save results to database
      const saved = await saveImport({
        total_stocks: smaData.results?.length || 0,
        stocks_with_signals: (smaData.summary?.buySignals || 0) + (smaData.summary?.sellSignals || 0),
        buy_signals: smaData.summary?.buySignals || 0,
        sell_signals: smaData.summary?.sellSignals || 0,
        analysis_results: {
          smaResults: persistedSmaResults,
          dailyIchimokuResults: persistedDailyIchimokuResults,
          weeklyIchimokuResults: persistedWeeklyIchimokuResults,
          smaSummary: smaData.summary || null,
          dailyIchimokuSummary: dailyData.summary || null,
          weeklyIchimokuSummary: weeklyData.summary || null
        }
      });
      
      // Save price histories to separate tables for instant chart loading on restore
      if (saved) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          // Get the run_id from the saved import
          const { data: latestImport } = await supabase
            .from('sma_surveillance_imports')
            .select('id')
            .eq('user_id', user.id)
            .order('imported_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          
          if (latestImport?.id) {
            // Prepare SMA price history records
            const smaPriceHistoryRecords: { user_id: string; run_id: string; ticker: string; price_history: { date: string; close: number }[] }[] = [];
            
            for (const result of (smaData.results || []) as SMAResult[]) {
              if (result.priceHistory && result.priceHistory.length > 0) {
                smaPriceHistoryRecords.push({
                  user_id: user.id,
                  run_id: latestImport.id,
                  ticker: result.ticker,
                  price_history: result.priceHistory
                });
              }
            }
            
            // Prepare Ichimoku price history records (keyed by timeframe_ticker)
            type IchimokuHistoryRecord = { user_id: string; run_id: string; ticker: string; price_history: IchimokuResult['priceHistory'] };
            const ichimokuPriceHistoryRecords: IchimokuHistoryRecord[] = [];
            
            for (const result of (dailyData.results || []) as IchimokuResult[]) {
              if (result.priceHistory && result.priceHistory.length > 0) {
                ichimokuPriceHistoryRecords.push({
                  user_id: user.id,
                  run_id: latestImport.id,
                  ticker: `daily_${result.ticker}`,
                  price_history: result.priceHistory
                });
              }
            }
            
            for (const result of (weeklyData.results || []) as IchimokuResult[]) {
              if (result.priceHistory && result.priceHistory.length > 0) {
                ichimokuPriceHistoryRecords.push({
                  user_id: user.id,
                  run_id: latestImport.id,
                  ticker: `weekly_${result.ticker}`,
                  price_history: result.priceHistory
                });
              }
            }
            
            // Batch insert SMA price histories
            const BATCH_SIZE = 50;
            for (let i = 0; i < smaPriceHistoryRecords.length; i += BATCH_SIZE) {
              const batch = smaPriceHistoryRecords.slice(i, i + BATCH_SIZE);
              await supabase.from('surveillance_price_history').upsert(batch, {
                onConflict: 'run_id,ticker'
              });
            }
            
            // Batch insert Ichimoku price histories (cast to JSON-compatible format)
            for (let i = 0; i < ichimokuPriceHistoryRecords.length; i += BATCH_SIZE) {
              const batch = ichimokuPriceHistoryRecords.slice(i, i + BATCH_SIZE).map(rec => ({
                user_id: rec.user_id,
                run_id: rec.run_id,
                ticker: rec.ticker,
                price_history: JSON.parse(JSON.stringify(rec.price_history))
              }));
              await supabase.from('ichimoku_price_history').upsert(batch, {
                onConflict: 'run_id,ticker'
              });
            }
            
            console.log(`Saved ${smaPriceHistoryRecords.length} SMA + ${ichimokuPriceHistoryRecords.length} Ichimoku price histories for run ${latestImport.id}`);
          }
        }
      }

      if (!saved) {
        toast({
          title: "Auto-save failed",
          description: "Your scan completed, but saving it to the database failed. Please try again (and ensure you are logged in).",
          variant: "destructive",
        });
      }

      // Send comprehensive email report with all data
      const { data: { user } } = await supabase.auth.getUser();
      // Fetch correlation data for the scan report (if enabled)
      let correlationData = null;
      if (correlationInReportEnabled) {
        const scanTickers = ((persistedSmaResults || []) as any[]).map((r) => String(r?.ticker || '')).filter((t) => t.length > 0);
        const uniqueTickers = scanTickers.filter((t, i) => scanTickers.indexOf(t) === i);
        correlationData = await fetchCorrelationForReport(uniqueTickers);
      }

      supabase.functions.invoke('send-scan-report', {
        body: {
          scanType: 'sma_surveillance',
          smaResults: persistedSmaResults,
          smaSummary: smaData.summary || null,
          dailyIchimokuResults: dailyData.results || [],
          weeklyIchimokuResults: weeklyData.results || [],
          dailyIchimokuSummary: dailyData.summary || null,
          weeklyIchimokuSummary: weeklyData.summary || null,
          userEmail: user?.email,
          recipientEmail: reportRecipientEmail.trim() || undefined,
          correlationData: correlationData || undefined,
        }
      }).then(response => {
        if (response.error) {
          console.error('Failed to send scan report email:', response.error);
        } else {
          console.log('Scan report email sent successfully');
        }
      });
      
      // Send checklist email with stocks on alert (if enabled)
      if (checklistEmailEnabled) {
        supabase.functions.invoke('send-checklist-report', {
          body: {
            smaResults: smaData.results || [],
            dailyIchimokuResults: dailyData.results || [],
            weeklyIchimokuResults: weeklyData.results || [],
            source: 'scan',
            userEmail: user?.email,
            recipientEmail: reportRecipientEmail.trim() || undefined,
            correlationData: correlationInReportEnabled ? correlationData : undefined,
          }
        }).then(response => {
          if (response.error) {
            console.error('Failed to send checklist email:', response.error);
          } else {
            console.log('Checklist email sent successfully');
            toast({
              title: "Checklist Report Sent",
              description: "Check List SMA 2/12 & Ichimoku emailed to you",
            });
          }
        });
      }
      
      toast({
        title: "Scan Complete & Saved",
        description: `Analyzed ${smaData.results?.length || 0} stocks. Data saved and email report sent.`,
      });
      playBell();
    } catch (error) {
      console.error('Scan error:', error);
      toast({
        title: "Scan Failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setTimeout(() => {
        setLoading(false);
        setProgress(0);
      }, 500);
    }
  };

  const getSignalBadge = (signal: 'BUY' | 'SELL' | 'NEUTRAL', hasCrossover: boolean) => {
    if (signal === 'BUY') {
      return (
        <Badge className="bg-green-500 hover:bg-green-600">
          <TrendingUp className="w-3 h-3 mr-1" />
          BUY {hasCrossover && '🔄'}
        </Badge>
      );
    }
    if (signal === 'SELL') {
      return (
        <Badge className="bg-red-500 hover:bg-red-600">
          <TrendingDown className="w-3 h-3 mr-1" />
          SELL {hasCrossover && '🔄'}
        </Badge>
      );
    }
    return <Badge variant="secondary">NEUTRAL</Badge>;
  };

  const getAlertBadges = (alerts: IchimokuResult['alerts']) => {
    if (alerts.length === 0) {
      return <Badge variant="outline" className="text-muted-foreground">No alerts</Badge>;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {alerts.map((alert, idx) => {
          const config = alertTypeLabels[alert.type];
          return (
            <Badge key={idx} className={`${config.color} text-white text-xs`}>
              {config.label}
            </Badge>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <LoadSavedDataDialog
        open={showSavedDataDialog}
        onOpenChange={setShowSavedDataDialog}
        onConfirm={handleLoadSavedData}
        onDecline={handleDeclineSavedData}
        importedAt={lastImport.importedAt}
        dataType="IBKR Surveillance"
      />
      
      
    <div className="min-h-screen bg-background">
      {loading && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-foreground min-w-[200px]">
                Scanning SMA & Ichimoku...
              </span>
              <Progress value={progress} className="flex-1" />
              <span className="text-sm font-semibold text-primary min-w-[50px] text-right">
                {progress}%
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
                <Search className="h-8 w-8 text-primary" />
                IBKR Surveillance
              </h1>
              <p className="text-muted-foreground">
                SMA crossovers + Ichimoku cloud alerts (daily & weekly)
              </p>
              {lastImport.importedAt && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <LastImportBadge importedAt={lastImport.importedAt} label="Last analysis" />
                  {preloadedChartsCount && (preloadedChartsCount.sma > 0 || preloadedChartsCount.ichimoku > 0) && (
                    <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                      <Activity className="w-3 h-3 mr-1" />
                      {preloadedChartsCount.sma + preloadedChartsCount.ichimoku} charts ready
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <SavedDataMenu />
            <AutoSaveIndicator />
            <div className="flex items-center gap-2">
              <input
                type="email"
                placeholder="Recipient email (default)"
                value={reportRecipientEmail}
                onChange={(e) => {
                  setReportRecipientEmail(e.target.value);
                  localStorage.setItem('sma_report_recipient', e.target.value);
                }}
                className="h-8 w-44 rounded-md border border-input bg-background px-2 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <button
              onClick={toggleChecklistEmail}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                checklistEmailEnabled 
                  ? 'bg-primary/10 text-primary hover:bg-primary/20' 
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
              title={checklistEmailEnabled ? "Checklist emails are enabled" : "Checklist emails are disabled"}
            >
              {checklistEmailEnabled ? (
                <>
                  <MailCheck className="w-4 h-4" />
                  <span className="hidden sm:inline">Emails ON</span>
                </>
              ) : (
                <>
                  <MailX className="w-4 h-4" />
                  <span className="hidden sm:inline">Emails OFF</span>
                </>
              )}
            </button>
            <button
              onClick={toggleCorrelationInReport}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                correlationInReportEnabled 
                  ? 'bg-primary/10 text-primary hover:bg-primary/20' 
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
              title={correlationInReportEnabled ? "Correlation analysis included in reports" : "Correlation analysis disabled in reports"}
            >
              {correlationInReportEnabled ? (
                <>
                  <Activity className="w-4 h-4" />
                  <span className="hidden sm:inline">Corr ON</span>
                </>
              ) : (
                <>
                  <Activity className="w-4 h-4" />
                  <span className="hidden sm:inline">Corr OFF</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Upload & Scan Controls */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Import Stock Tickers</CardTitle>
            <CardDescription>
              Upload an Excel file with stock tickers in column A, or load your last saved analysis
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Load Saved Data Button - Always show when saved data exists */}
            {lastImport.importedAt && savedData?.analysis_results && (
              <LoadSavedDataButton
                importedAt={lastImport.importedAt}
                onLoad={handleLoadSavedData}
                loading={loadingSaved}
                label="Load Last Analysis"
              />
            )}
            <div className="flex flex-wrap gap-4 items-center">
              {/* CSV Transform Button */}
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCsvTransformAuto}
                  className="hidden"
                />
                <div className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors">
                  <FileSpreadsheet className="h-4 w-4" />
                  Transform CSV
                </div>
              </label>

              {/* Excel Upload Button */}
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <div className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors">
                  <Upload className="h-4 w-4" />
                  Upload Excel File
                </div>
              </label>

              {tickers.length > 0 && (
                <Badge variant="outline" className="text-sm">
                  {tickers.length} tickers loaded
                </Badge>
              )}

              <Button
                onClick={handleScan}
                disabled={loading || tickers.length === 0}
                className="ml-auto"
              >
                {loading ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Activity className="mr-2 h-4 w-4" />
                )}
                Run Full Analysis
              </Button>
            </div>

            {tickers.length > 0 && (
              <div className="mt-4 p-3 bg-muted rounded-md">
                <p className="text-sm text-muted-foreground mb-2">Loaded tickers:</p>
                <div className="flex flex-wrap gap-1">
                  {tickers.slice(0, 20).map((ticker, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">
                      {ticker}
                    </Badge>
                  ))}
                  {tickers.length > 20 && (
                    <Badge variant="secondary" className="text-xs">
                      +{tickers.length - 20} more
                    </Badge>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results Tabs */}
        {(smaResults.length > 0 || dailyIchimokuResults.length > 0) && (
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'sma' | 'ichimoku')}>
            <div className="flex items-center justify-between mb-4">
              <TabsList>
                <TabsTrigger value="sma" className="gap-2">
                  <TrendingUp className="h-4 w-4" />
                  SMA Analysis
                </TabsTrigger>
                <TabsTrigger value="ichimoku" className="gap-2">
                  <Cloud className="h-4 w-4" />
                  Ichimoku Alerts
                </TabsTrigger>
              </TabsList>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportToCSV}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  CSV
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportToExcel}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Excel
                </Button>
              </div>
            </div>

            {/* SMA Tab */}
            <TabsContent value="sma">
              {/* SMA Summary Cards */}
              {smaSummary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-3xl font-bold text-foreground">{smaSummary.total}</div>
                      <div className="text-sm text-muted-foreground">Total Scanned</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-500/10 border-green-500/30">
                    <CardContent className="pt-6 text-center">
                      <div className="text-3xl font-bold text-green-600">{smaSummary.buySignals}</div>
                      <div className="text-sm text-green-600">Buy Signals</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-500/10 border-red-500/30">
                    <CardContent className="pt-6 text-center">
                      <div className="text-3xl font-bold text-red-600">{smaSummary.sellSignals}</div>
                      <div className="text-sm text-red-600">Sell Signals</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-500/10 border-blue-500/30">
                    <CardContent className="pt-6 text-center">
                      <div className="text-3xl font-bold text-blue-600">{smaSummary.crossovers}</div>
                      <div className="text-sm text-blue-600">Recent Crossovers</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-3xl font-bold text-muted-foreground">{smaSummary.errors}</div>
                      <div className="text-sm text-muted-foreground">Errors</div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* SMA Filter */}
              <div className="flex justify-end mb-4">
                <Button
                  variant={showCrossoversOnly ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowCrossoversOnly(!showCrossoversOnly)}
                  className="gap-2"
                >
                  <Filter className="h-4 w-4" />
                  {showCrossoversOnly ? 'Showing Crossovers' : 'Show Crossovers Only'}
                  {showCrossoversOnly && (
                    <Badge variant="secondary" className="ml-1">
                      {filteredSmaResults.length}
                    </Badge>
                  )}
                </Button>
              </div>

              {/* SMA Table */}
              <Card>
                <CardHeader>
                  <CardTitle>SMA 2/12 Analysis</CardTitle>
                  <CardDescription>
                    Click on a row to view the price chart. 🔄 indicates a crossover in the last 2 sessions.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {filteredSmaResults.length === 0 && showCrossoversOnly ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No recent crossovers detected in the scanned stocks.
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Ticker</TableHead>
                          <TableHead className="text-right">Price</TableHead>
                          <TableHead className="text-right">SMA 2</TableHead>
                          <TableHead className="text-right">SMA 12</TableHead>
                          <TableHead>Signal 2/12</TableHead>
                          <TableHead className="text-right">SMA 50</TableHead>
                          <TableHead className="text-right">SMA 200</TableHead>
                          <TableHead>Signal 50/200</TableHead>
                          <TableHead>Bollinger</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredSmaResults.map((result, index) => (
                          <TableRow
                            key={index}
                            className={`cursor-pointer hover:bg-muted/50 ${selectedSmaStock?.ticker === result.ticker ? 'bg-muted' : ''} ${loadingPriceHistory ? 'pointer-events-none opacity-50' : ''}`}
                            onClick={() => handleSelectSmaStock(result)}
                          >
                            <TableCell className="font-semibold">{result.ticker}</TableCell>
                            <TableCell className="text-right">${result.currentPrice.toFixed(2)}</TableCell>
                            <TableCell className="text-right">{result.sma2.toFixed(2)}</TableCell>
                            <TableCell className="text-right">{result.sma12.toFixed(2)}</TableCell>
                            <TableCell>{getSignalBadge(result.signal2_12, result.crossover2_12)}</TableCell>
                            <TableCell className="text-right">{result.sma50.toFixed(2)}</TableCell>
                            <TableCell className="text-right">{result.sma200 > 0 ? result.sma200.toFixed(2) : 'N/A'}</TableCell>
                            <TableCell>{getSignalBadge(result.signal50_200, result.crossover50_200)}</TableCell>
                            <TableCell>
                              {result.bollingerSignal === 'BUY' ? (
                                <Badge className="bg-green-500 hover:bg-green-600">
                                  <TrendingUp className="w-3 h-3 mr-1" />
                                  BUY
                                </Badge>
                              ) : result.bollingerSignal === 'SELL' ? (
                                <Badge className="bg-red-500 hover:bg-red-600">
                                  <TrendingDown className="w-3 h-3 mr-1" />
                                  SELL
                                </Badge>
                              ) : (
                                <Badge variant="secondary">—</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {result.error ? (
                                <Badge variant="destructive" className="text-xs">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Error
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs">OK</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Ichimoku Tab */}
            <TabsContent value="ichimoku">
              {/* Ichimoku Summary Cards */}
              {ichimokuSummary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-3xl font-bold text-foreground">{ichimokuSummary.total}</div>
                      <div className="text-sm text-muted-foreground">Total Scanned</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-500/10 border-red-500/30">
                    <CardContent className="pt-6 text-center">
                      <div className="text-3xl font-bold text-red-600">{ichimokuSummary.kumoBreakDown}</div>
                      <div className="text-sm text-red-600">Kumo Break ↓</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-yellow-500/10 border-yellow-500/30">
                    <CardContent className="pt-6 text-center">
                      <div className="text-3xl font-bold text-yellow-600">{ichimokuSummary.withinKumo}</div>
                      <div className="text-sm text-yellow-600">Within Kumo</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-500/10 border-blue-500/30">
                    <CardContent className="pt-6 text-center">
                      <div className="text-3xl font-bold text-blue-600">{ichimokuSummary.tenkanTouch}</div>
                      <div className="text-sm text-blue-600">Tenkan Touch</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-500/10 border-purple-500/30">
                    <CardContent className="pt-6 text-center">
                      <div className="text-3xl font-bold text-purple-600">{ichimokuSummary.kijunTouch}</div>
                      <div className="text-sm text-purple-600">Kijun Touch</div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Ichimoku Timeframe + Filter */}
              <div className="flex items-center justify-between mb-4">
                <Tabs value={ichimokuTimeframe} onValueChange={(v) => setIchimokuTimeframe(v as 'daily' | 'weekly')}>
                  <TabsList>
                    <TabsTrigger value="daily">Daily</TabsTrigger>
                    <TabsTrigger value="weekly">Weekly</TabsTrigger>
                  </TabsList>
                </Tabs>
                <Button
                  variant={showAlertsOnly ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowAlertsOnly(!showAlertsOnly)}
                  className="gap-2"
                >
                  <AlertTriangle className="h-4 w-4" />
                  {showAlertsOnly ? 'Showing Alerts Only' : 'Show Alerts Only'}
                  {showAlertsOnly && (
                    <Badge variant="secondary" className="ml-1">
                      {filteredIchimokuResults.length}
                    </Badge>
                  )}
                </Button>
              </div>

              {/* Ichimoku Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Cloud className="h-5 w-5 text-primary" />
                    Ichimoku Cloud Alerts ({ichimokuTimeframe})
                  </CardTitle>
                  <CardDescription>
                    Click on a row to view the Ichimoku chart with cloud visualization
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {filteredIchimokuResults.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      {showAlertsOnly ? 'No Ichimoku alerts detected.' : 'Run analysis to see results.'}
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Ticker</TableHead>
                          <TableHead className="text-right">Price</TableHead>
                          <TableHead className="text-right">Tenkan</TableHead>
                          <TableHead className="text-right">Kijun</TableHead>
                          <TableHead className="text-right">Span A</TableHead>
                          <TableHead className="text-right">Span B</TableHead>
                          <TableHead>Alerts</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredIchimokuResults.map((result, index) => (
                          <TableRow
                            key={index}
                            className={`cursor-pointer hover:bg-muted/50 ${selectedIchimokuStock?.ticker === result.ticker ? 'bg-muted' : ''} ${loadingIchimokuHistory ? 'pointer-events-none opacity-50' : ''}`}
                            onClick={() => handleSelectIchimokuStock(result)}
                          >
                            <TableCell className="font-semibold">{result.ticker}</TableCell>
                            <TableCell className="text-right">${result.currentPrice.toFixed(2)}</TableCell>
                            <TableCell className="text-right text-blue-600">${result.tenkanSen.toFixed(2)}</TableCell>
                            <TableCell className="text-right text-purple-600">${result.kijunSen.toFixed(2)}</TableCell>
                            <TableCell className="text-right text-green-600">${result.senkouSpanA.toFixed(2)}</TableCell>
                            <TableCell className="text-right text-red-600">${result.senkouSpanB.toFixed(2)}</TableCell>
                            <TableCell>{getAlertBadges(result.alerts)}</TableCell>
                            <TableCell>
                              {result.error ? (
                                <Badge variant="destructive" className="text-xs">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Error
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs">OK</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        {/* SMA Chart Modal */}
        <Dialog open={!!selectedSmaStock} onOpenChange={(open) => !open && setSelectedSmaStock(null)}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            {selectedSmaStock && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3 text-2xl">
                    <span className="font-bold">{selectedSmaStock.ticker}</span>
                    <Badge variant="outline" className="text-lg px-3 py-1">
                      ${selectedSmaStock.currentPrice.toFixed(2)}
                    </Badge>
                    {selectedSmaStock.signal2_12 === 'BUY' && (
                      <Badge className="bg-green-500">
                        <TrendingUp className="w-4 h-4 mr-1" /> BUY Signal
                      </Badge>
                    )}
                    {selectedSmaStock.signal2_12 === 'SELL' && (
                      <Badge className="bg-red-500">
                        <TrendingDown className="w-4 h-4 mr-1" /> SELL Signal
                      </Badge>
                    )}
                  </DialogTitle>
                  <DialogDescription>
                    SMA Analysis with Bollinger Bands
                  </DialogDescription>
                </DialogHeader>

                {/* SMA Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-4">
                  <Card className="bg-purple-500/10 border-purple-500/30">
                    <CardContent className="p-3 text-center">
                      <div className="text-lg font-bold text-purple-600">${selectedSmaStock.sma2.toFixed(2)}</div>
                      <div className="text-xs text-purple-600">SMA 2</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-500/10 border-amber-500/30">
                    <CardContent className="p-3 text-center">
                      <div className="text-lg font-bold text-amber-600">${selectedSmaStock.sma12.toFixed(2)}</div>
                      <div className="text-xs text-amber-600">SMA 12</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-500/10 border-blue-500/30">
                    <CardContent className="p-3 text-center">
                      <div className="text-lg font-bold text-blue-600">${selectedSmaStock.sma50.toFixed(2)}</div>
                      <div className="text-xs text-blue-600">SMA 50</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-500/10 border-red-500/30">
                    <CardContent className="p-3 text-center">
                      <div className="text-lg font-bold text-red-600">
                        {selectedSmaStock.sma200 > 0 ? `$${selectedSmaStock.sma200.toFixed(2)}` : 'N/A'}
                      </div>
                      <div className="text-xs text-red-600">SMA 200</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Main Chart */}
                <SMAStockChart result={selectedSmaStock} />
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Ichimoku Chart Modal */}
        <Dialog open={!!selectedIchimokuStock} onOpenChange={(open) => !open && setSelectedIchimokuStock(null)}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            {selectedIchimokuStock && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3 text-2xl">
                    <Cloud className="h-6 w-6 text-primary" />
                    <span className="font-bold">{selectedIchimokuStock.ticker}</span>
                    <Badge variant="outline" className="text-lg px-3 py-1">
                      ${selectedIchimokuStock.currentPrice.toFixed(2)}
                    </Badge>
                    <Badge variant="secondary">{selectedIchimokuStock.timeframe}</Badge>
                  </DialogTitle>
                  <DialogDescription>
                    Ichimoku Cloud Analysis with alert markers
                  </DialogDescription>
                </DialogHeader>

                <IchimokuSurveillanceChart result={selectedIchimokuStock} />
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
    </>
  );
};

// Helper function to calculate SMA at each data point
const calculateChartData = (priceHistory: { date: string; close: number }[], periods: number[]) => {
  const reversed = [...priceHistory].reverse();
  const bollingerPeriod = 20;
  const stdDevMultiplier = 2;
  
  return reversed.map((point, index) => {
    const result: { 
      date: string; 
      close: number; 
      sma2?: number; 
      sma12?: number; 
      sma50?: number; 
      sma200?: number;
      bollingerUpper?: number;
      bollingerMiddle?: number;
      bollingerLower?: number;
    } = {
      date: point.date,
      close: point.close,
    };
    
    // Calculate SMAs
    periods.forEach(period => {
      if (index >= period - 1) {
        const slice = reversed.slice(index - period + 1, index + 1);
        const sum = slice.reduce((acc, p) => acc + p.close, 0);
        const smaKey = `sma${period}` as 'sma2' | 'sma12' | 'sma50' | 'sma200';
        result[smaKey] = sum / period;
      }
    });
    
    // Calculate Bollinger Bands
    if (index >= bollingerPeriod - 1) {
      const slice = reversed.slice(index - bollingerPeriod + 1, index + 1);
      const closes = slice.map(p => p.close);
      const mean = closes.reduce((sum, c) => sum + c, 0) / bollingerPeriod;
      const squaredDiffs = closes.map(c => Math.pow(c - mean, 2));
      const variance = squaredDiffs.reduce((sum, d) => sum + d, 0) / bollingerPeriod;
      const stdDev = Math.sqrt(variance);
      
      result.bollingerMiddle = mean;
      result.bollingerUpper = mean + (stdDevMultiplier * stdDev);
      result.bollingerLower = mean - (stdDevMultiplier * stdDev);
    }
    
    return result;
  });
};

// SMA Stock Chart component
const SMAStockChart = ({ result }: { result: SMAResult }) => {
  const [timeRange, setTimeRange] = useState<'1M' | '3M' | '6M' | '1Y' | 'ALL'>('ALL');

  const filteredData = useMemo(() => {
    if (!result.priceHistory || result.priceHistory.length === 0) {
      return [];
    }
    const allData = calculateChartData(result.priceHistory, [2, 12, 50, 200]);
    
    if (timeRange === 'ALL') return allData;
    
    const daysMap = { '1M': 30, '3M': 90, '6M': 180, '1Y': 365 };
    const days = daysMap[timeRange];
    
    return allData.slice(-days);
  }, [result.priceHistory, timeRange]);

  const timeRangeButtons = [
    { label: '1M', value: '1M' as const },
    { label: '3M', value: '3M' as const },
    { label: '6M', value: '6M' as const },
    { label: '1Y', value: '1Y' as const },
    { label: 'All', value: 'ALL' as const },
  ];

  // Show message if no price history available
  if (!result.priceHistory || result.priceHistory.length === 0) {
    return (
      <div className="h-[400px] w-full flex items-center justify-center bg-muted/30 rounded-lg border border-dashed">
        <div className="text-center text-muted-foreground">
          <p className="text-lg font-medium">Chart data not available</p>
          <p className="text-sm">Price history could not be loaded for this stock.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Time Range Selector */}
      <div className="flex items-center justify-end gap-1">
        {timeRangeButtons.map((btn) => (
          <Button
            key={btn.value}
            variant={timeRange === btn.value ? "default" : "outline"}
            size="sm"
            onClick={() => setTimeRange(btn.value)}
            className="h-7 px-3 text-xs"
          >
            {btn.label}
          </Button>
        ))}
      </div>
      
      <div className="h-[400px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={filteredData}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis
              dataKey="date"
              tickFormatter={(value) => {
                const date = new Date(value);
                return `${date.getMonth() + 1}/${date.getDate()}`;
              }}
              tick={{ fontSize: 11 }}
              interval="preserveStartEnd"
            />
            <YAxis
              domain={['auto', 'auto']}
              tick={{ fontSize: 11 }}
              tickFormatter={(value) => `$${value.toFixed(0)}`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--popover))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
              formatter={(value: number, name: string) => {
                const labels: Record<string, string> = {
                  close: 'Price',
                  sma2: 'SMA 2',
                  sma12: 'SMA 12',
                  sma50: 'SMA 50',
                  sma200: 'SMA 200',
                  bollingerUpper: 'BB Upper',
                  bollingerMiddle: 'BB Middle (SMA 20)',
                  bollingerLower: 'BB Lower',
                };
                return [`$${value?.toFixed(2) || 'N/A'}`, labels[name] || name];
              }}
              labelFormatter={(label) => new Date(label).toLocaleDateString()}
            />
            <Legend />
            {/* Bollinger Bands - Area Fill */}
            <Area
              type="monotone"
              dataKey="bollingerUpper"
              stroke="transparent"
              fill="#10b981"
              fillOpacity={0.15}
              name="BB Band"
            />
            <Area
              type="monotone"
              dataKey="bollingerLower"
              stroke="transparent"
              fill="hsl(var(--background))"
              fillOpacity={1}
              legendType="none"
            />
            {/* Bollinger Bands - Upper Line */}
            <Line
              type="monotone"
              dataKey="bollingerUpper"
              name="BB Upper"
              stroke="#10b981"
              strokeWidth={1}
              dot={false}
              strokeDasharray="4 2"
              legendType="none"
            />
            {/* Bollinger Bands - Middle (SMA 20) */}
            <Line
              type="monotone"
              dataKey="bollingerMiddle"
              name="BB Middle"
              stroke="#6b7280"
              strokeWidth={1}
              dot={false}
              strokeDasharray="2 2"
            />
            {/* Bollinger Bands - Lower Line */}
            <Line
              type="monotone"
              dataKey="bollingerLower"
              name="BB Lower"
              stroke="#10b981"
              strokeWidth={1}
              dot={false}
              strokeDasharray="4 2"
              legendType="none"
            />
            {/* Price */}
            <Line
              type="monotone"
              dataKey="close"
              name="Price"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="sma2"
              name="SMA 2"
              stroke="#a855f7"
              strokeWidth={1.5}
              dot={false}
              strokeDasharray="2 2"
            />
            <Line
              type="monotone"
              dataKey="sma12"
              name="SMA 12"
              stroke="#f59e0b"
              strokeWidth={1.5}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="sma50"
              name="SMA 50"
              stroke="#3b82f6"
              strokeWidth={1.5}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="sma200"
              name="SMA 200"
              stroke="#ef4444"
              strokeWidth={1.5}
              dot={false}
            />
            {/* Brush for zoom/scroll */}
            <Brush
              dataKey="date"
              height={30}
              stroke="hsl(var(--primary))"
              fill="hsl(var(--muted))"
              tickFormatter={(value) => {
                const date = new Date(value);
                return `${date.getMonth() + 1}/${date.getDate()}`;
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SMASurveillance;
